import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import Box from '@material-ui/core/Box';
import {
    Typography,
    withStyles,
    CircularProgress,
    styled
} from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import { Link } from 'react-router-dom';
import LoaderButton from '../components/LoaderButton';
import TicketTable from '../components/TicketTable';
import globalStyles from '../styles';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2)
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1)
    }
}));

const BootstrapDialogTitle = props => {
    const { classes, children, onClose, ...other } = props;

    return (
        <DialogTitle {...other} className={classes.title}>
            <Box style={{ display: 'flex' }}>
                <Box>{children}</Box>
                <Box style={{ display: 'inline-flex', width: 30 }}>
                    {onClose ? (
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            className={classes.closeBtn}
                        >
                            <Icon>close</Icon>
                        </IconButton>
                    ) : null}
                </Box>
            </Box>
        </DialogTitle>
    );
};

const styles = theme => ({
    root: {
        maxWidth: '1200px',
        margin: '20px auto',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px'
        }
    },
    heading: {
        marginBottom: '20px'
    },
    error: {
        margin: '20px 0',
        fontSize: '18px',
        color: red[500]
    },
    noTickets: {
        maxWidth: 400,
        margin: '0 auto',
        '& > p': {
            margin: '20px auto',
            fontSize: '18px'
        }
    },
    button: {
        marginTop: '10px'
    },
    loadMoreButton: {
        marginTop: 20
    },
    faq: {
        listStyleType: 'none',
        padding: 0
    },
    faqContent: {
        '& p': {
            margin: '20px auto',
            fontSize: '18px'
        }
    },
    closeBtn: {
        position: 'absolute',
        right: 8,
        top: 8
    },
    title: {
        padding: '16px 16px'
    },
    link: globalStyles.link
});

class ViewTickets extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            loadingMore: false,
            tickets: [],
            lastItem: null,
            renewals: {},
            loadMoreError: false,
            openFaq: 0
        };
    }

    async componentDidMount() {
        try {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const response = await axios.get(
                `${
                    process.env.REACT_APP_API_ENDPOINT
                }/customer-ticket/list-by/user-id/${
                    currentSession.getAccessToken().payload.sub
                }`,
                {
                    headers
                }
            );

            const renewalsResponse = await axios.get(
                `${process.env.REACT_APP_RENEWAL_API_ENDPOINT}/renewal/user/${
                    currentSession.getAccessToken().payload.sub
                }`,
                {
                    headers
                }
            );

            const renewals = {};

            renewalsResponse.data.forEach(renewal => {
                renewals[renewal.customer_ticket_id] = renewal;
            });

            this.setState({
                tickets: response.data.items,
                lastItem: response.data.last_item,
                renewals,
                loading: false,
                error: false
            });
        } catch (e) {
            this.setState({
                loading: false,
                error: true
            });
        }
    }

    async loadMoreTickets() {
        try {
            const currentSession = await Auth.currentSession();

            const headers = {
                Authorization: currentSession.getIdToken().getJwtToken()
            };

            const { lastItem, tickets } = this.state;

            const response = await axios.get(
                `${
                    process.env.REACT_APP_API_ENDPOINT
                }/customer-ticket/list-by/user-id/${
                    currentSession.getAccessToken().payload.sub
                }?lastKey=${encodeURIComponent(JSON.stringify(lastItem))}`,
                {
                    headers
                }
            );

            this.setState({
                tickets: [...tickets, ...response.data.items],
                lastItem: response.data.last_item,
                loadingMore: false,
                loadMoreError: false
            });
        } catch (e) {
            this.setState({
                loadingMore: false,
                loadMoreError: true
            });
        }
    }

    handleClose() {
        this.setState({
            openFaq: 0
        });
    }

    render() {
        const {
            tickets,
            renewals,
            loading,
            loadingMore,
            loadMoreError,
            lastItem,
            error,
            openFaq
        } = this.state;
        const { classes } = this.props;

        if (loading) {
            return (
                <div className={classes.root}>
                    <CircularProgress id="loading-spinner" />
                </div>
            );
        }
        return (
            <div className={classes.root}>
                <BootstrapDialog
                    onClose={() => {
                        this.handleClose();
                    }}
                    aria-labelledby="customized-dialog-title"
                    open={openFaq > 0}
                    PaperProps={{
                        style: {
                            margin: 16,
                            minWidth: 300
                        }
                    }}
                >
                    <BootstrapDialogTitle
                        id="customized-dialog-title"
                        onClose={() => {
                            this.handleClose();
                        }}
                        classes={classes}
                    >
                        FAQ
                    </BootstrapDialogTitle>
                    <DialogContent dividers className={classes.faqContent}>
                        {openFaq === 1 && (
                            <>
                                <Typography variant="h5">
                                    How to buy my ticket
                                </Typography>
                                <Typography variant="body2">
                                    Pulhams Coaches can give you a link to the
                                    ticket listing where you can purchase your
                                    ticket.
                                </Typography>
                                <Typography variant="body2">
                                    Pulhams Coaches may promote the ticket
                                    listing links on their own website, on
                                    social media, or they may have emailed it
                                    directly to you.
                                </Typography>
                                <Typography variant="body2">
                                    If you have not been given access to the
                                    ticket listing, please contact Pulhams
                                    Coaches directly, who will be able to help.
                                </Typography>
                            </>
                        )}
                        {openFaq === 2 && (
                            <>
                                <Typography variant="h5">
                                    Who is my Transport Provider?
                                </Typography>
                                <Typography variant="body2">
                                    Pulhams Coaches (Transport Provider) is the
                                    organisation that arranges your transport
                                    and is who you will pay directly.
                                </Typography>
                                <Typography variant="body2">
                                    ShuttleID is a ticketing platform that helps
                                    Pulhams Coaches to sell bus tickets online.
                                </Typography>
                            </>
                        )}
                        {openFaq === 3 && (
                            <>
                                <Typography variant="h5">
                                    How do I apply my bursary?
                                </Typography>
                                <Typography variant="body2">
                                    If you have a bursary or financial support
                                    that pays for your travel, either partially
                                    or in full, Pulhams Coaches will either need
                                    to supply you with a special link to buy a
                                    discounted ticket, or will need to provide
                                    you with a voucher code which can be applied
                                    at the checkout.
                                </Typography>
                                <Typography variant="body2">
                                    Please contact Pulhams Coaches directly if
                                    you have not received either a link to your
                                    discounted ticket, or your voucher code.
                                </Typography>
                            </>
                        )}
                        {openFaq === 4 && (
                            <>
                                <Typography variant="h5">
                                    How do I apply my voucher code?
                                </Typography>
                                <Typography variant="body2">
                                    When buying a ticket, voucher codes are
                                    applied at the final stage of the checkout
                                    before confirming payment.
                                </Typography>
                                <Typography variant="body2">
                                    If you are using Direct Debit as your
                                    payment method, please note this option is
                                    after you create your initial Direct Debit
                                    mandate. After, you will be given the
                                    opportunity to enter the passenger details,
                                    apply your voucher code, and then confirm
                                    the order.
                                </Typography>
                                <Typography variant="body2">
                                    Further information on{' '}
                                    <a
                                        className={classes.link}
                                        href="https://help.shuttleid.uk/en/articles/7852190-how-to-apply-a-voucher-code"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        how to apply your voucher code
                                    </a>{' '}
                                    is available in the Help Portal.
                                </Typography>
                            </>
                        )}
                        {openFaq === 5 && (
                            <>
                                <Typography variant="h5">
                                    I exited my Direct Debit ticket purchase too
                                    early. How do I go back?
                                </Typography>
                                <Typography variant="body2">
                                    Tickets purchased using Direct Debit require
                                    a Direct Debit mandate to be initially set
                                    up.
                                </Typography>
                                <Typography variant="body2">
                                    The mandate set up itself does not create
                                    any payments, but it does give permission
                                    for Pulhams Coaches to make charges in
                                    future. We use this mandate to schedule
                                    payments automatically, after purchasing
                                    your ticket.
                                </Typography>
                                <Typography variant="body2">
                                    If you left the ticket purchase after
                                    creating the Direct Debit mandate, you will
                                    have a Direct Debit mandate, but you
                                    won&apos;t have a ticket, and no payments
                                    will be scheduled.
                                </Typography>
                                <Typography variant="body2">
                                    To complete your purchase, simply attempt
                                    your purchase again using the same process
                                    as before (e.g. clicking a link in an email
                                    from Pulhams Coaches, or a link from our
                                    website). The next time you do this, it will
                                    skip the Direct Debit mandate form as you
                                    have already created a mandate. Instead, you
                                    will be asked to complete the details for
                                    the ticket purchase (e.g. passenger name,
                                    photo, bus stop, etc).
                                </Typography>
                                <Typography variant="body2">
                                    Completing your purchase will give you
                                    instant access to your ticket, and you will
                                    receive email confirmation of your order.
                                </Typography>
                                <Typography variant="body2">
                                    Further help on{' '}
                                    <a
                                        className={classes.link}
                                        href="https://help.shuttleid.uk/en/articles/7852300-how-to-buy-a-ticket-using-direct-debit"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        how to buy your ticket using Direct
                                        Debit
                                    </a>{' '}
                                    is available on the Help Portal.
                                </Typography>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            autoFocus
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                this.handleClose();
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </BootstrapDialog>

                <Typography
                    className={classes.heading}
                    variant="h4"
                    color="primary"
                >
                    My Tickets
                </Typography>
                {error && (
                    <Typography className={classes.error}>
                        There was a problem retrieving your tickets
                    </Typography>
                )}
                {tickets.length === 0 && !error ? (
                    <div className={classes.noTickets}>
                        <Typography variant="body2">
                            After purchasing your tickets, you&apos;ll be able
                            to access them here in the My Tickets area.
                        </Typography>
                        <Typography variant="h5">
                            Frequently asked questions
                        </Typography>
                        <ul className={classes.faq}>
                            <li>
                                <Button
                                    onClick={() => {
                                        this.setState({ openFaq: 1 });
                                    }}
                                >
                                    + How to buy my ticket
                                </Button>
                            </li>
                            <li>
                                <Button
                                    onClick={() => {
                                        this.setState({ openFaq: 2 });
                                    }}
                                >
                                    + Who is my Transport Provider?
                                </Button>
                            </li>
                            <li>
                                <Button
                                    onClick={() => {
                                        this.setState({ openFaq: 3 });
                                    }}
                                >
                                    + How do I apply my bursary?
                                </Button>
                            </li>
                            <li>
                                <Button
                                    onClick={() => {
                                        this.setState({ openFaq: 4 });
                                    }}
                                >
                                    + How do I apply my voucher code?
                                </Button>
                            </li>
                            <li>
                                <Button
                                    onClick={() => {
                                        this.setState({ openFaq: 5 });
                                    }}
                                >
                                    + I exited my Direct Debit ticket purchase
                                    too early. How do I go back?
                                </Button>
                            </li>
                            <li>
                                <Button component={Link} to="/contact">
                                    + I need further help
                                </Button>
                            </li>
                        </ul>
                    </div>
                ) : (
                    <>
                        <TicketTable tickets={tickets} renewals={renewals} />
                        {loadMoreError && (
                            <Typography className={classes.error}>
                                There was a problem retrieving your tickets.
                                Please try again shortly.
                            </Typography>
                        )}
                        {lastItem && (
                            <LoaderButton
                                className={classes.loadMoreButton}
                                variant="outlined"
                                color="primary"
                                type="button"
                                disabled={loadingMore}
                                isLoading={loadingMore}
                                onClick={() => {
                                    this.setState({
                                        loadingMore: true
                                    });

                                    this.loadMoreTickets();
                                }}
                                text="Load More"
                                loadingText="Loading..."
                            />
                        )}
                    </>
                )}
            </div>
        );
    }
}

ViewTickets.propTypes = {
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(ViewTickets);
